import { useCallback, useState } from 'react'
import { Button } from '~/components/common/Button'
import { Typography } from '~/components/common/Typography'
import { cm } from '~/utils/utils'

/**
 * Durable, maintained tutorial that walks a user through creating the Azure
 * service principal the deploy step needs, then connecting it.
 *
 * Intentionally CLI-first with a conceptual stepper and NO Azure-portal
 * screenshots: `az ad sp create-for-rbac` and the trust model are stable across
 * portal redesigns, so this content ages gracefully. This is a fixed component
 * (not generated per task) so it can't drift or hallucinate — update it
 * deliberately if Azure changes the command surface.
 */
export interface ConnectAzureGuideProps {
  /** Target subscription id to splice into the command, if already known. */
  subscriptionId?: string
  /** Opens the Connect Azure form / navigates to the connections page. */
  onConnect?: () => void
  className?: string
}

const FIELD_MAP: { out: string; field: string }[] = [
  { out: 'appId', field: 'Client ID' },
  { out: 'password', field: 'Client Secret' },
  { out: 'tenant', field: 'Tenant ID' },
  { out: '(the subscription above)', field: 'Subscription ID' }
]

const StepNumber = ({ n }: { n: number }) => (
  <span
    className='flex size-6 shrink-0 items-center justify-center rounded-full bg-tokens-status-link-3 text-[12px] font-medium text-tokens-status-link-1'
    aria-hidden='true'
  >
    {n}
  </span>
)

export const ConnectAzureGuide = ({
  subscriptionId,
  onConnect,
  className
}: ConnectAzureGuideProps) => {
  const [copied, setCopied] = useState(false)

  const sub = subscriptionId || '<YOUR_SUBSCRIPTION_ID>'
  const command = `az ad sp create-for-rbac --name "curie-deploy" --role Contributor --scopes /subscriptions/${sub}`

  const copy = useCallback(() => {
    void navigator.clipboard?.writeText(command).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }, [command])

  return (
    <div className={cm('flex flex-col gap-4', className)} data-testid='connect-azure-guide'>
      <div>
        <Typography fontFamily='geist' fontSize='titleSm' fontWeight='bold'>
          Connect Azure to deploy
        </Typography>
        <Typography
          fontFamily='geist'
          fontSize='bodySm'
          className='mt-1 block text-neutral-text-secondary'
        >
          Curie deploys into your Azure with a credential you create — scoped to one subscription.
          It never gets broader access than the subscription you pick.
        </Typography>
      </div>

      {/* Step 1 — create the SP (CLI: stable across portal changes) */}
      <div className='flex gap-3'>
        <StepNumber n={1} />
        <div className='min-w-0 flex-1'>
          <Typography fontFamily='geist' fontSize='bodySm' fontWeight='medium'>
            Create a service principal (Contributor on your subscription)
          </Typography>
          <div className='mt-2 rounded-[12px] border border-tokens-neutral-border-2 bg-tokens-neutral-bg-action'>
            <pre
              className='overflow-x-auto whitespace-pre-wrap break-all p-3 font-mono text-[12px] leading-5 text-tokens-status-success-3'
              data-testid='connect-azure-command'
            >
              {command}
            </pre>
            <div className='flex justify-end border-t border-tokens-neutral-border-1 p-2'>
              <Button variant='borderless' onClick={copy}>
                {copied ? 'Copied' : 'Copy command'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Step 2 — map the output to the connection fields */}
      <div className='flex gap-3'>
        <StepNumber n={2} />
        <div className='min-w-0 flex-1'>
          <Typography fontFamily='geist' fontSize='bodySm' fontWeight='medium'>
            Map the command output to the connection fields
          </Typography>
          <table className='mt-2 w-full text-[13px]'>
            <tbody>
              {FIELD_MAP.map(({ out, field }) => (
                <tr key={field} className='border-b border-tokens-neutral-border-2 last:border-0'>
                  <td className='py-1.5 pr-3 font-mono text-neutral-text-secondary'>{out}</td>
                  <td className='py-1.5 text-neutral-text-tertiary'>→</td>
                  <td className='py-1.5 pl-1 text-neutral-text-primary'>{field}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Step 3 — connect */}
      <div className='flex gap-3'>
        <StepNumber n={3} />
        <div className='min-w-0 flex-1'>
          <Typography fontFamily='geist' fontSize='bodySm' fontWeight='medium'>
            Paste them into Curie
          </Typography>
          <Typography
            fontFamily='geist'
            fontSize='labelSm'
            className='mt-1 block text-neutral-text-secondary'
          >
            The client secret is shown once by Azure — copy it before closing the terminal.
          </Typography>
          {onConnect && (
            <Button variant='primary' className='mt-2' onClick={onConnect}>
              Connect Azure
            </Button>
          )}
        </div>
      </div>

      <Typography fontFamily='geist' fontSize='labelSm' className='text-neutral-text-tertiary'>
        The CLI is the supported path — it stays stable as the Azure portal changes. You can also do
        this in the portal (App registrations → new registration → add a client secret → assign the
        Contributor role), but its exact screens move over time.
      </Typography>
    </div>
  )
}

export default ConnectAzureGuide
